import { browserEnvironment } from '../../lib/supabase';
import { commandEndpoint } from '../../lib/command-endpoint';

export type PublicCatalogue = {
  business: { slug: string; name: string; currency: string; timeZone: string };
  policy: {
    cancellationAllowed: boolean;
    rescheduleAllowed: boolean;
    cancellationNoticeMinutes: number;
    rescheduleNoticeMinutes: number;
    holdMinutes: number;
  };
  locations: {
    id: string;
    name: string;
    city: string | null;
    region: string | null;
    timeZone: string;
  }[];
  services: {
    id: string;
    name: string;
    description: string | null;
    durationMinutes: number;
    grossAmountMinor: number;
    currency: string;
  }[];
  staff: { id: string; displayName: string; biography: string | null }[];
};

export type PublicSlot = {
  location_id: string;
  service_id: string;
  staff_id: string;
  starts_at: string;
  ends_at: string;
  local_starts_at: string;
  time_zone: string;
};

export type ManagedBooking = {
  id: string;
  status: string;
  startsAt: string;
  endsAt: string;
  holdExpiresAt: string | null;
  business: { slug: string; name: string };
  location: { id: string; name: string; timeZone: string };
  staff: { id: string; displayName: string };
  service: {
    id: string;
    name: string;
    durationMinutes: number;
    grossAmountMinor: number;
    currency: string;
  };
  policy: PublicCatalogue['policy'];
  payment: {
    totalMinor: number;
    paidMinor: number;
    refundedMinor: number;
    outstandingMinor: number;
    currency: string;
    state: string;
    methodFamily: string | null;
  };
  paymentOptions: PaymentOption[];
};

export type PaymentOption = {
  option: 'card_deposit' | 'card_full' | 'afterpay_full';
  label: string;
  amountMinor: number;
  currency: string;
  methodFamily: 'card' | 'afterpay';
};

export type PreparedPayment = {
  attemptId: string;
  clientSecret: string;
  publishableKey: string;
  option: PaymentOption['option'];
  amountMinor: number;
  currency: string;
  connectedAccountId: string;
};

async function command<T>(body: Record<string, unknown>, managementToken?: string): Promise<T> {
  const response = await fetch(
    commandEndpoint(browserEnvironment.VITE_SUPABASE_URL, 'public-booking'),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: browserEnvironment.VITE_SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${browserEnvironment.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        ...(managementToken ? { 'x-booking-token': managementToken } : {})
      },
      body: JSON.stringify(body)
    }
  );
  const result = (await response.json()) as T & { error?: string };
  if (!response.ok) throw new Error(result.error ?? 'Booking request failed');
  return result;
}

export const getCatalogue = (slug: string) =>
  command<PublicCatalogue>({ action: 'catalogue', slug });

export async function createPublicSession(slug: string) {
  return command<{ sessionToken: string }>({ action: 'session', slug });
}

export async function getPublicAvailability(input: {
  slug: string;
  locationId: string;
  serviceId: string;
  staffId?: string;
  date: string;
}) {
  const anchor = new Date(`${input.date}T00:00:00Z`).getTime();
  return command<{ slots: PublicSlot[] }>({
    action: 'availability',
    ...input,
    rangeStart: new Date(anchor - 14 * 60 * 60 * 1000).toISOString(),
    rangeEnd: new Date(anchor + 14 * 60 * 60 * 1000).toISOString()
  });
}

export async function createPublicHold(input: {
  slug: string;
  sessionToken: string;
  turnstileToken?: string;
  slot: PublicSlot;
  requestId: string;
  givenName: string;
  familyName: string;
  email: string;
  phone: string;
}) {
  return command<{ booking_id: string; hold_expires_at: string; managementToken: string }>({
    action: 'create-hold',
    slug: input.slug,
    sessionToken: input.sessionToken,
    turnstileToken: input.turnstileToken,
    locationId: input.slot.location_id,
    serviceId: input.slot.service_id,
    staffId: input.slot.staff_id,
    startsAt: input.slot.starts_at,
    requestId: input.requestId,
    givenName: input.givenName,
    familyName: input.familyName,
    email: input.email,
    phone: input.phone
  });
}

export const getManagedBooking = (token: string) =>
  command<ManagedBooking>({ action: 'get-booking' }, token);

export const cancelManagedBooking = (token: string, reason: string) =>
  command<ManagedBooking>({ action: 'cancel', requestId: crypto.randomUUID(), reason }, token);

export const rescheduleManagedBooking = (token: string, slot: PublicSlot) =>
  command<ManagedBooking>(
    {
      action: 'reschedule',
      requestId: crypto.randomUUID(),
      locationId: slot.location_id,
      staffId: slot.staff_id,
      startsAt: slot.starts_at
    },
    token
  );

export async function createPaymentIntent(
  token: string,
  option: PaymentOption['option'],
  requestId: string
) {
  const response = await fetch(
    commandEndpoint(browserEnvironment.VITE_SUPABASE_URL, 'stripe-payment'),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: browserEnvironment.VITE_SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${browserEnvironment.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        'x-booking-token': token
      },
      body: JSON.stringify({ action: 'create-intent', option, requestId })
    }
  );
  const result = (await response.json()) as PreparedPayment & { error?: string };
  if (!response.ok) throw new Error(result.error ?? 'Payment setup failed');
  return result;
}
